import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "metamorph_cart_v1";
const TICKET_PRICE = { sydney: 100, melbourne: 100, adelaide: 100, london: 85, paris: 115 };
const VINYL_PRICE = 60;

const CartContext = createContext(null);

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch (e) {
    return [];
  }
}

function persist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) { /* ignore */ }
}

function keyOf(item) {
  if (item.type === "ticket") return `ticket:${item.city}`;
  if (item.type === "vinyl") return "vinyl";
  if (item.type === "wav") return "wav";
  if (item.type === "myth_film") return "myth_film";
  return item.type;
}

function unitPriceOf(item) {
  if (item.type === "ticket") return TICKET_PRICE[item.city] ?? 0;
  if (item.type === "vinyl") return VINYL_PRICE;
  if (item.type === "wav") return Number(item.custom_price || 0);
  if (item.type === "myth_film") return Number(item.custom_price || 0);
  return 0;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    persist(items);
  }, [items]);

  const add = useCallback((item) => {
    setItems((prev) => {
      const k = keyOf(item);
      const existing = prev.find((i) => keyOf(i) === k);
      if (existing) {
        // WAV / Myth Film: replace custom price; tickets/vinyl: bump qty
        if (item.type === "wav" || item.type === "myth_film") {
          return prev.map((i) =>
            keyOf(i) === k ? { ...i, custom_price: item.custom_price } : i
          );
        }
        return prev.map((i) =>
          keyOf(i) === k ? { ...i, quantity: (i.quantity || 1) + (item.quantity || 1) } : i
        );
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setOpen(true);
  }, []);

  const setQuantity = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (keyOf(i) === key ? { ...i, quantity: Math.max(0, qty) } : i))
        .filter((i) => (i.quantity || 0) > 0)
    );
  }, []);

  const updateWavPrice = useCallback((price) => {
    setItems((prev) =>
      prev.map((i) => (i.type === "wav" ? { ...i, custom_price: Number(price) || 0 } : i))
    );
  }, []);

  const remove = useCallback((key) => {
    setItems((prev) => prev.filter((i) => keyOf(i) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((acc, i) => acc + unitPriceOf(i) * (i.type === "wav" || i.type === "myth_film" ? 1 : i.quantity || 1), 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((acc, i) => acc + (i.type === "wav" || i.type === "myth_film" ? 1 : i.quantity || 1), 0),
    [items]
  );
  const hasVinyl = items.some((i) => i.type === "vinyl");
  const hasWav = items.some((i) => i.type === "wav");
  const hasTicket = items.some((i) => i.type === "ticket");

  const value = useMemo(
    () => ({
      items,
      open,
      setOpen,
      add,
      remove,
      setQuantity,
      updateWavPrice,
      clear,
      subtotal,
      count,
      hasVinyl,
      hasWav,
      hasTicket,
      keyOf,
    }),
    [items, open, add, remove, setQuantity, updateWavPrice, clear, subtotal, count, hasVinyl, hasWav, hasTicket]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
